import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { AuthContext } from '../contexts/AuthContext';
import { 
  FaBuilding, 
  FaMapMarkedAlt, 
  FaChartLine, 
  FaFilePdf, 
  FaCreditCard,
  FaShieldAlt,
  FaBrain,
  FaClock,
  FaGlobe,
  FaIndustry,
  FaCloudDownloadAlt,
  FaUserTie,
  FaRocket,
  FaServer,
  FaBolt
} from 'react-icons/fa';

const Features = () => {
  const { isAuthenticated } = useContext(AuthContext);
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "RiskDiscover AI Risk Assessment Platform",
    "applicationCategory": "BusinessApplication",
    "description": "Advanced AI-powered risk assessment platform with comprehensive features for insurance professionals and business analysts",
    "operatingSystem": "Web Browser",
    "audience": {
      "@type": "BusinessAudience",
      "audienceType": "Insurance Professionals"
    },
    "areaServed": "Europe",
    "provider": {
      "@type": "Organization",
      "name": "moredevs.ai",
      "url": "https://moredevs.ai"
    },
    "softwareVersion": "2.0",
    "releaseNotes": "Enhanced AI algorithms and expanded European company database",
    "featureList": [
      "AI-powered company intelligence gathering",
      "Location-based risk mapping with satellite imagery",
      "Dynamic risk scoring (A-F grade system)",
      "Industry-specific risk benchmarking",
      "Professional PDF report generation",
      "Real-time data processing under 2 minutes",
      "European company database coverage",
      "Multi-factor risk evaluation algorithms",
      "Comprehensive business profile analysis",
      "Geographic hazard identification",
      "Regulatory compliance risk assessment",
      "Market volatility analysis"
    ],
    "applicationSubCategory": "Risk Management Software",
    "permissions": "No special permissions required",
    "processingTime": "PT2M",
    "supportedLanguage": ["en-US", "en-GB"],
    "browserRequirements": "Modern web browser with JavaScript enabled"
  };
  
  const features = [
    {
      category: "Core Capabilities",
      items: [
        {
          icon: <FaBuilding className="text-3xl" />,
          title: "Company Intelligence",
          description: "Comprehensive company data gathering from verified public sources, including business registration, financial indicators, and corporate structure.",
          color: "from-blue-500 to-cyan-500",
          bgLight: "bg-blue-50",
          textColor: "text-blue-600"
        },
        {
          icon: <FaMapMarkedAlt className="text-3xl" />,
          title: "Location Risk Mapping",
          description: "Visual geographic risk assessment with satellite imagery analysis to identify location-specific hazards and environmental factors.",
          color: "from-green-500 to-teal-500",
          bgLight: "bg-green-50",
          textColor: "text-green-600"
        },
        {
          icon: <FaBrain className="text-3xl" />,
          title: "AI-Powered Analysis",
          description: "Advanced machine learning algorithms analyze multiple risk dimensions to provide accurate, data-driven risk assessments.",
          color: "from-purple-500 to-pink-500",
          bgLight: "bg-purple-50",
          textColor: "text-purple-600"
        },
        {
          icon: <FaChartLine className="text-3xl" />,
          title: "Dynamic Risk Scoring",
          description: "Real-time risk scoring across location and industry-specific factors with detailed grade breakdowns from A to F.",
          color: "from-orange-500 to-red-500",
          bgLight: "bg-orange-50",
          textColor: "text-orange-600"
        }
      ]
    },
    {
      category: "Risk Assessment Features",
      items: [
        {
          icon: <FaGlobe className="text-3xl" />,
          title: "Location Risk Analysis",
          description: "Evaluate natural disasters, climate risks, infrastructure quality, and geopolitical stability for any business location.",
          color: "from-blue-500 to-indigo-500",
          bgLight: "bg-blue-50",
          textColor: "text-blue-600"
        },
        {
          icon: <FaIndustry className="text-3xl" />,
          title: "Sector-Specific Insights",
          description: "Industry-tailored risk assessments covering regulatory compliance, market volatility, and sector-specific challenges.",
          color: "from-green-500 to-emerald-500",
          bgLight: "bg-green-50",
          textColor: "text-green-600"
        },
        {
          icon: <FaShieldAlt className="text-3xl" />,
          title: "Key Takeaways for Insurers",
          description: "Actionable insights specifically designed for insurance professionals to make informed underwriting decisions.",
          color: "from-purple-500 to-violet-500",
          bgLight: "bg-purple-50",
          textColor: "text-purple-600"
        },
        {
          icon: <FaClock className="text-3xl" />,
          title: "Rapid Processing",
          description: "Get comprehensive risk assessments in under 2 minutes, enabling quick decision-making without compromising accuracy.",
          color: "from-yellow-500 to-orange-500",
          bgLight: "bg-yellow-50",
          textColor: "text-yellow-600"
        }
      ]
    },
    {
      category: "Platform Benefits",
      items: [
        {
          icon: <FaCreditCard className="text-3xl" />,
          title: "Flexible Credit System",
          description: "Simple and transparent annual credit system. Each assessment uses one credit. Credits refresh yearly with your subscription.",
          color: "from-indigo-500 to-purple-500",
          bgLight: "bg-indigo-50",
          textColor: "text-indigo-600"
        },
        {
          icon: <FaFilePdf className="text-3xl" />,
          title: "Professional Reports",
          description: "Export detailed risk assessment reports as PDF documents, perfect for client presentations and compliance records.",
          color: "from-red-500 to-pink-500",
          bgLight: "bg-red-50",
          textColor: "text-red-600"
        },
        {
          icon: <FaCloudDownloadAlt className="text-3xl" />,
          title: "Data Portability",
          description: "Download and archive all your assessments. Your data remains accessible whenever you need it.",
          color: "from-cyan-500 to-blue-500",
          bgLight: "bg-cyan-50",
          textColor: "text-cyan-600"
        },
        {
          icon: <FaUserTie className="text-3xl" />,
          title: "Built for Professionals",
          description: "Designed specifically for insurance brokers, underwriters, and risk managers who need reliable, fast assessments.",
          color: "from-gray-500 to-slate-500",
          bgLight: "bg-gray-50",
          textColor: "text-gray-600"
        }
      ]
    }
  ];

  const metrics = [
    { 
      icon: <FaRocket className="text-3xl text-blue-200 mx-auto mb-4" />,
      value: "90%", 
      label: "Faster than traditional methods" 
    },
    { 
      icon: <FaChartLine className="text-3xl text-blue-200 mx-auto mb-4" />,
      value: "15+", 
      label: "Risk factors analyzed" 
    },
    { 
      icon: <FaServer className="text-3xl text-blue-200 mx-auto mb-4" />,
      value: "24/7", 
      label: "Platform availability" 
    },
    { 
      icon: <FaBolt className="text-3xl text-blue-200 mx-auto mb-4" />,
      value: "<2min", 
      label: "Average assessment time" 
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Advanced Risk Assessment Features | RiskDiscover AI Platform"
        description="Discover powerful AI-driven risk assessment features. Location-based analysis, industry insights, and comprehensive reporting tools."
        canonical="/features"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:40px_40px]"></div>
          
          {/* Animated circles */}
          <div className="absolute top-20 left-20 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-40 right-20 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-20 left-40 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-center">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 mb-8">
              <FaBrain className="text-yellow-300 mr-2" />
              <span className="text-white text-sm font-medium">Advanced AI Risk Intelligence</span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Powerful Features for
              <span className="block bg-gradient-to-r from-blue-200 to-purple-200 bg-clip-text text-transparent">
                Intelligent Discovery
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto leading-relaxed">
              Leverage cutting-edge AI technology to transform how you evaluate business risks. 
              Fast, accurate, and comprehensive assessments at your fingertips.
            </p>
            
            <Link
              to={isAuthenticated ? "/dashboard" : "/auth/signup"}
              className="group bg-white text-blue-700 px-10 py-5 rounded-full font-semibold text-lg hover:shadow-2xl transition-all transform hover:scale-105 inline-flex items-center"
            >
              {isAuthenticated ? "Go to Dashboard" : "Get Started"}
              <FaRocket className="ml-3 group-hover:translate-x-2 transition-transform" />
            </Link>
          </div>
        </div>
        
        {/* Add CSS for animations */}
        <style jsx>{`
          @keyframes blob {
            0% { transform: translate(0px, 0px) scale(1); }
            33% { transform: translate(30px, -50px) scale(1.1); }
            66% { transform: translate(-20px, 20px) scale(0.9); }
            100% { transform: translate(0px, 0px) scale(1); }
          }
          
          .animate-blob {
            animation: blob 7s infinite;
          }
          
          .animation-delay-2000 {
            animation-delay: 2s;
          }
          
          .animation-delay-4000 {
            animation-delay: 4s;
          }
        `}</style>
      </section>

      {/* Metrics Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-12 text-white">
            <h3 className="text-2xl font-semibold text-center mb-10 text-blue-100">
              Performance Metrics That Matter
            </h3>
            <div className="grid md:grid-cols-4 gap-8 text-center">
              {metrics.map((metric, index) => (
                <div key={index} className="group">
                  {metric.icon}
                  <div className="text-4xl font-bold mb-2 group-hover:scale-110 transition-transform">
                    {metric.value}
                  </div>
                  <div className="text-blue-100">{metric.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Sections */}
      {features.map((section, sectionIndex) => (
        <section key={sectionIndex} className="py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">{section.category}</h2>
              <p className="text-xl text-gray-600">
                {sectionIndex === 0 ? "Essential capabilities that power your risk assessments" : 
                 sectionIndex === 1 ? "Advanced features for comprehensive risk analysis" : 
                 "Everything you need for a seamless experience"}
              </p>
            </div>
            <div className="grid lg:grid-cols-2 gap-8">
              {section.items.map((feature, index) => (
                <div key={index} className="group relative">
                  {/* Gradient border effect */}
                  <div className={`absolute -inset-0.5 bg-gradient-to-r ${feature.color} rounded-2xl opacity-0 group-hover:opacity-100 transition duration-500 blur`}></div>
                  
                  {/* Card Content */}
                  <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                    {/* Icon */}
                    <div className={`${feature.bgLight} ${feature.textColor} w-14 h-14 rounded-xl flex items-center justify-center mb-6`}>
                      {feature.icon}
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {feature.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                    
                    {/* Bottom accent */}
                    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.color} rounded-b-2xl`}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* How It Works Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Simple, fast, and powerful risk assessment process</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Enter Company Info", desc: "Provide fiscal number and location", color: "from-blue-500 to-cyan-500" },
              { step: "2", title: "AI Analysis", desc: "Our system gathers and analyzes data", color: "from-purple-500 to-pink-500" },
              { step: "3", title: "Risk Calculation", desc: "Advanced algorithms compute risk scores", color: "from-green-500 to-teal-500" },
              { step: "4", title: "Get Report", desc: "Receive detailed assessment instantly", color: "from-orange-500 to-red-500" }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className={`w-16 h-16 bg-gradient-to-r ${item.color} text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 group-hover:scale-110 transition-transform`}>
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-3xl p-12 text-white text-center">
            <FaRocket className="text-5xl mx-auto mb-6 animate-bounce" />
            <h2 className="text-4xl font-bold mb-6">Coming Soon: Premium & Enterprise Features</h2>
            <p className="text-xl mb-6 opacity-90">
              Portfolio risk assessment, multi-location analysis, API access, and more advanced features are on the way!
            </p>
            <p className="text-lg mb-8 opacity-80 italic">
              💡 Our developers run faster than our marketers, so we might have more features than we're telling you about. Let us surprise you!
            </p>
            <Link
              to="/products"
              className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:shadow-xl transition-all transform hover:scale-105"
            >
              View All Plans
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Risk Assessment?
          </h2>
          <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto">
            Join forward-thinking insurance professionals using AI-powered assessments
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={isAuthenticated ? "/dashboard" : "/auth/signup"}
              className="group bg-white text-blue-700 px-10 py-5 rounded-full font-semibold text-lg hover:shadow-2xl transition-all transform hover:scale-105 inline-flex items-center justify-center"
            >
              {isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
              <FaRocket className="ml-3 group-hover:translate-x-2 transition-transform" />
            </Link>
            <Link
              to="/products"
              className="border-2 border-white text-white px-10 py-5 rounded-full font-semibold text-lg hover:bg-white hover:text-blue-700 transition-all inline-flex items-center justify-center"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;